/**
 *
 * @file index.tsx
 *
 * @fileoverview A custom hook that helps with managing the entity Form. Takes all information about the form and
 * returns helper functions to organize groups, validate the form, process fields and filter excluded values.
 */

import { generateValidationSchema } from './helpers/validation';
import { FormWidgetProps } from '~/jobiqo/components/Form/Widgets/Form';
import {
  filterFieldGroupsPerBundle,
  filterHiddenFieldsPerBundle,
  orderFieldGroups,
  removeFlaggedFields,
} from '~/jobiqo/components/Form/Widgets/Form/hooks/helpers/fieldGroups';
import {
  filterExcludedFields,
  processFields,
} from '~/jobiqo/components/Form/Widgets/Form/hooks/helpers/fields';
import { getInitialValues } from '~/jobiqo/components/Form/Widgets/Form/hooks/helpers/getInitialValues';

export const useEntityFormHelpers = ({
  fieldGroups = [],
  excludedFieldList = [],
  entity,
  definition,
  overrides = {},
  bundle,
  additionalFieldList = [],
  extraValidationSchema,
  extraInitialValues,
}: Pick<
  FormWidgetProps,
  | 'fieldGroups'
  | 'excludedFieldList'
  | 'entity'
  | 'definition'
  | 'overrides'
  | 'bundle'
  | 'additionalFieldList'
  | 'extraInitialValues'
  | 'extraValidationSchema'
>): any => {
  // Clean the field groups by removing any flagged fields which flags
  // are set to false.
  let cleanedFieldGroups = removeFlaggedFields(fieldGroups, bundle);

  // Remove fields for a given bundle.
  cleanedFieldGroups = filterFieldGroupsPerBundle(cleanedFieldGroups, bundle);
  const cleanedOverrides = filterHiddenFieldsPerBundle(
    fieldGroups,
    overrides,
    bundle,
  );
  const cleanedExtraValidationSchema = filterHiddenFieldsPerBundle(
    fieldGroups,
    extraValidationSchema,
    bundle,
  );
  const cleanedExtraInitialValues = filterHiddenFieldsPerBundle(
    fieldGroups,
    extraInitialValues,
    bundle,
  );

  // Cleanup the entity definition that we get from the API
  // Excluding any fields that are set to false or to null so that they
  // are not taken in consideration. Uses the excludedFieldList but also the
  // above cleaned up field groups (which already includes null fields and flagged fields).
  const cleanedFields = filterExcludedFields(
    cleanedFieldGroups,
    excludedFieldList,
    definition.fields,
    definition,
  );
  const cleanedEntityDefinition = {
    ...definition,
    fields: cleanedFields,
  };

  return {
    getInitialValues: () =>
      getInitialValues(
        entity,
        cleanedEntityDefinition,
        cleanedOverrides,
        cleanedExtraInitialValues,
      ),
    processFields: (values) =>
      processFields(
        cleanedFieldGroups,
        cleanedEntityDefinition,
        excludedFieldList,
        values,
        additionalFieldList,
      ),
    orderFieldGroups: () =>
      orderFieldGroups(
        cleanedFieldGroups,
        cleanedEntityDefinition.fields,
        excludedFieldList,
      ),
    generateValidationSchema: () =>
      generateValidationSchema(
        cleanedFieldGroups,
        excludedFieldList,
        cleanedEntityDefinition,
        cleanedExtraValidationSchema,
      ),
  };
};
